interface StickyHeaderTableProps extends React.ComponentPropsWithoutRef<'table'> {
	children: React.ReactNode;
	emphasizeFirstColumn?: boolean;
	tableRef?: React.RefObject<HTMLTableElement>;
	variant?: 'data';
}

export type StickyHeaderTableSortOrder = 'asc' | 'desc';

interface StickyHeaderTableSortHeaderProps<SortKey extends string>
	extends Omit<React.ComponentPropsWithoutRef<'th'>, 'onChange'> {
	columnKey: SortKey;
	sortKey: SortKey;
	sortOrder: StickyHeaderTableSortOrder;
	onSortChange: (sort: { key: SortKey; order: StickyHeaderTableSortOrder }) => void;
}

export function StickyHeaderTableSortHeader<SortKey extends string>({
	children,
	columnKey,
	sortKey,
	sortOrder,
	onSortChange,
	className,
	...rest
}: StickyHeaderTableSortHeaderProps<SortKey>) {
	const selected = columnKey === sortKey;

	return (
		<th
			{...rest}
			className={`sticky-header-table-sort-header ${className ?? ''}`}
			aria-sort={selected ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
		>
			<button
				type="button"
				className="sticky-header-table-sort-button"
				onClick={() =>
					onSortChange({
						key: columnKey,
						order: selected && sortOrder === 'asc' ? 'desc' : 'asc',
					})
				}
			>
				<span>{children}</span>
				<i
					className={selected ? `fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}` : 'fas fa-sort'}
					aria-hidden="true"
				/>
			</button>
		</th>
	);
}

export default function StickyHeaderTable(props: StickyHeaderTableProps) {
	const { children, className, emphasizeFirstColumn, tableRef, variant, ...rest } = props;

	return (
		<table
			className={`sticky-header-table ${variant ? `sticky-header-table--${variant}` : ''} ${emphasizeFirstColumn ? 'emphasize-first-column' : ''} ${className ?? ''}`}
			ref={tableRef}
			{...rest}
		>
			{children}
		</table>
	);
}
